import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import {
  getBillingAccount,
  getMe,
  listBreakdown,
  listSpendByAgent,
  listUsage,
  setAgentBudget,
  type AgentSpend,
  type BillingAccount,
  type SpendLine,
  type UsageEntry,
} from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/toast";
import { Empty, ErrorState, PageHeader, Spinner } from "@/components/Page";
import { cn } from "@/lib/utils";

// Display: whole credits when large, up to 2 decimals when small.
function fmtCredits(n: number): string {
  const abs = Math.abs(n);
  return n.toLocaleString(undefined, { maximumFractionDigits: abs > 0 && abs < 100 ? 2 : 0 });
}
function fmtUntil(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "soon";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// The "type" badge for a usage-breakdown line.
function lineBadge(l: SpendLine): string {
  switch (l.type) {
    case "scheduled":
      return "Scheduled";
    case "conversation":
      return "Platform chat";
    case "infra":
      return "Infrastructure";
    case "buildai":
      return "Build with AI";
    case "channel":
      return l.kind === "telegram" ? "Telegram" : l.kind === "slack" ? "Slack" : "Channel";
  }
  return l.type;
}

// ---- Overview: current credits ----

function BalanceCard({ acct }: { acct: BillingAccount }) {
  const cap = acct.cap_credits;
  const spend = acct.spendable_credits;
  const pct = cap > 0 ? Math.min(100, Math.max(0, Math.round((spend / cap) * 100))) : 0;
  const refill = acct.daily_refill_credits;
  // Bonus/granted credits above the daily cap don't expire, but they pause the daily
  // top-up (it only fills up TO the cap) — surface that so a funded workspace isn't
  // confused about why the daily allowance "isn't doing anything".
  const over = cap > 0 ? spend - cap : 0;
  const aboveCap = over > 0;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>Credits</CardTitle>
          {acct.plan && acct.plan_name && (
            <Badge variant="outline" className="capitalize">
              {acct.plan_name} plan
            </Badge>
          )}
        </div>
        <CardDescription>Agents spend from one pool that refills daily up to your cap. 1 credit ≈ $0.01.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-4xl font-semibold tracking-tight tabular-nums">{fmtCredits(spend)}</div>
            <div className="mt-1 text-sm text-muted-foreground">
              credits available
              {cap > 0 && (aboveCap ? <> · {fmtCredits(over)} above your cap</> : <> · of {fmtCredits(cap)} cap</>)}
            </div>
          </div>
          {acct.blocked ? (
            <Badge variant="outline" className="border-destructive/30 bg-destructive/10 text-destructive">
              Out of credits
            </Badge>
          ) : acct.low_balance ? (
            <Badge variant="outline" className="border-warning/40 bg-warning/10 text-warning">
              Low balance
            </Badge>
          ) : null}
        </div>

        {cap > 0 && (
          <div className="space-y-1.5">
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={cn("h-full rounded-full transition-all", acct.blocked ? "bg-destructive" : "bg-primary")}
                style={{ width: `${pct}%` }}
              />
            </div>
            {refill > 0 &&
              (aboveCap ? (
                <p className="text-xs text-muted-foreground">
                  Daily allowance +{fmtCredits(refill)}/day is paused while you're above your {fmtCredits(cap)} cap — your
                  extra {fmtCredits(over)} are banked and don't expire. It resumes once your balance drops below the cap.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Refills +{fmtCredits(refill)}/day up to {fmtCredits(cap)} (no rollover past the cap) · next in{" "}
                  {fmtUntil(acct.next_refill_at)}.
                </p>
              ))}
          </div>
        )}

        {acct.blocked && (
          <p className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            Your workspace is out of credits — agents are paused until the daily refill or more credits are added.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function BudgetDialog({ agent, onSaved }: { agent: AgentSpend; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [daily, setDaily] = useState(String(agent.daily_budget_credits || ""));
  const [monthly, setMonthly] = useState(String(agent.monthly_budget_credits || ""));
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  async function save() {
    setSaving(true);
    try {
      await setAgentBudget(agent.project_id, Math.max(0, Math.round(Number(daily) || 0)), Math.max(0, Math.round(Number(monthly) || 0)));
      toast.success("Budget updated");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(humanizeError(e));
    } finally {
      setSaving(false);
    }
  }
  const has = agent.daily_budget_credits > 0 || agent.monthly_budget_credits > 0;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={has ? "outline" : "default"} size="sm" className="h-7 px-2.5 text-xs">
          {has ? "Edit budget" : "Set budget"}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Spending budget · {agent.name}</DialogTitle>
          <DialogDescription>
            Pause this agent when it spends more than its budget (it resumes when the period resets). 0 = no limit. 1 credit ≈ $0.01.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <label className="text-sm font-medium">Daily budget (credits)</label>
            <Input type="number" min="0" value={daily} onChange={(e) => setDaily(e.target.value)} placeholder="0 = no daily limit" />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Monthly budget (credits)</label>
            <Input type="number" min="0" value={monthly} onChange={(e) => setMonthly(e.target.value)} placeholder="0 = no monthly limit" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => void save()} disabled={saving}>
            {saving ? <Spinner className="h-4 w-4" /> : "Save budget"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function budgetCell(spent: number, cap: number) {
  if (cap <= 0) return <span className="text-muted-foreground">—</span>;
  const over = spent >= cap;
  return (
    <span className={cn("tabular-nums", over ? "text-destructive" : "text-foreground")}>
      {fmtCredits(spent)} / {fmtCredits(cap)}
    </span>
  );
}

function AgentBudgetsCard({ agents, onChanged }: { agents: AgentSpend[]; onChanged: () => void }) {
  // Only real agents (a project) can carry a budget; show those.
  const rows = agents.filter((a) => a.project_id !== "");
  return (
    <Card>
      <CardHeader>
        <CardTitle>Agent budgets</CardTitle>
        <CardDescription>
          Per-agent spending limits (daily / monthly). When an agent passes a budget it pauses until the period resets — the
          workspace cap still applies on top. 30-day spend shown for reference.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <Empty>No agents yet.</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead className="text-right">Spent (30d)</TableHead>
                <TableHead className="text-right">Daily budget</TableHead>
                <TableHead className="text-right">Monthly budget</TableHead>
                <TableHead className="text-right">&nbsp;</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.project_id}>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{fmtCredits(a.credits)}</TableCell>
                  <TableCell className="text-right">{budgetCell(a.daily_spent_credits, a.daily_budget_credits)}</TableCell>
                  <TableCell className="text-right">{budgetCell(a.monthly_spent_credits, a.monthly_budget_credits)}</TableCell>
                  <TableCell className="text-right">
                    <BudgetDialog agent={a} onSaved={onChanged} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ---- History ----

type BarRow = { label: string; sub?: string; credits: number };

// RankList renders a ranked spend breakdown as a plain label · badge · amount
// list (no bars — a comparison gauge here just read as noise next to the real
// activity log below it).
function RankList({ rows, loading }: { rows: BarRow[]; loading?: boolean }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Spinner /> Loading…
      </div>
    );
  }
  if (rows.length === 0) return <Empty>No usage in this window.</Empty>;
  return (
    <div className="divide-y">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm" title={r.label}>
              {r.label}
            </span>
            {r.sub && (
              <Badge variant="outline" className="shrink-0 text-[10px] text-muted-foreground">
                {r.sub}
              </Badge>
            )}
          </div>
          <span className="shrink-0 tabular-nums text-sm">{fmtCredits(r.credits)}</span>
        </div>
      ))}
    </div>
  );
}

// A short label for where/how a single charge happened — channel first (the
// thing the user wanted to see: Telegram/Slack/platform chat), then source.
function usageBadge(e: UsageEntry): string {
  if (e.channel === "telegram") return "Telegram";
  if (e.channel === "slack") return "Slack";
  if (e.channel === "dashboard") return "Platform chat";
  switch (e.source) {
    case "playbook_chat":
      return "Build with AI";
    case "compute":
      return "Infrastructure";
    case "storage":
      return "Storage";
    case "admin":
      return "Admin";
    case "daily_allowance":
      return "Daily allowance";
  }
  if (e.kind === "grant") return "Grant";
  if (e.kind === "refund") return "Refund";
  return e.source || e.kind;
}
function fmtWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Column template shared by the history header + rows so they stay aligned; min-w
// lets the box scroll horizontally on narrow widths instead of collapsing.
const HISTORY_COLS =
  "grid min-w-[620px] grid-cols-[14px_140px_minmax(0,1fr)_120px_92px_92px] items-center gap-2";

// UsageDetail is the expanded inline detail for one ledger row.
function UsageDetail({ entry: e }: { entry: UsageEntry }) {
  const credit = e.credits >= 0;
  const rows: { k: string; v: string }[] = [
    { k: "When", v: new Date(e.ts).toLocaleString() },
    ...(e.agent ? [{ k: "Agent", v: e.agent }] : []),
    { k: "Source", v: usageBadge(e) },
    ...(e.channel ? [{ k: "Channel", v: e.channel }] : []),
    { k: "Type", v: e.kind },
    ...(e.model ? [{ k: "Model", v: e.model }] : []),
    { k: "Credits", v: (credit ? "+" : "−") + fmtCredits(Math.abs(e.credits)) },
    { k: "Balance after", v: fmtCredits(e.balance_after_credits) },
  ];
  return (
    <div className="min-w-0 space-y-2">
      {e.description && <p className="whitespace-pre-wrap break-words text-sm">{e.description}</p>}
      <dl className="space-y-1.5 rounded-md border bg-muted/20 p-3 text-sm">
        {rows.map((r) => (
          <div key={r.k} className="flex min-w-0 justify-between gap-4">
            <dt className="shrink-0 text-muted-foreground">{r.k}</dt>
            <dd className="min-w-0 truncate text-right font-mono text-xs">{r.v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// HistoryTable is the per-charge ledger: newest-first, cursor-paged. GCP-logs style —
// a FIXED-HEIGHT box that lazy-loads more as you scroll near the bottom (so the page
// height never grows and the breakdown cards below stay reachable), and each row
// expands in place to show the full detail.
function HistoryTable() {
  const [entries, setEntries] = useState<UsageEntry[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const cursorRef = useRef<string | undefined>(undefined); // for the scroll handler (no stale closure)
  const fetchingRef = useRef(false); // single in-flight guard (scroll fires rapidly)

  const loadPage = useCallback((c?: string) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    const first = !c;
    if (first) setLoading(true);
    else setMore(true);
    setErr(null);
    listUsage({ cursor: c, limit: 50 })
      .then((r) => {
        setEntries((prev) => (first ? r.entries : [...prev, ...r.entries]));
        setCursor(r.next_cursor);
        cursorRef.current = r.next_cursor;
      })
      .catch((e) => setErr(humanizeError(e)))
      .finally(() => {
        setLoading(false);
        setMore(false);
        fetchingRef.current = false;
      });
  }, []);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const onScroll = useCallback(
    (ev: React.UIEvent<HTMLDivElement>) => {
      const el = ev.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 240 && cursorRef.current && !fetchingRef.current) {
        loadPage(cursorRef.current);
      }
    },
    [loadPage],
  );

  if (loading)
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Spinner /> Loading…
      </div>
    );
  if (err) return <ErrorState message={err} onRetry={() => loadPage()} />;
  if (entries.length === 0) return <Empty>No activity yet.</Empty>;

  return (
    <div className="h-[28rem] overflow-auto rounded-md border" onScroll={onScroll}>
      <div
        className={cn(
          HISTORY_COLS,
          "sticky top-0 z-10 border-b bg-card px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground",
        )}
      >
        <span />
        <span>When</span>
        <span>Activity</span>
        <span>Source</span>
        <span className="text-right">Credits</span>
        <span className="text-right">Balance</span>
      </div>
      <ul className="divide-y">
        {entries.map((e) => {
          const open = openId === e.id;
          const credit = e.credits >= 0;
          return (
            <li key={e.id} className={cn(open && "bg-muted/30")}>
              <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpenId(open ? null : e.id)}
                className={cn(HISTORY_COLS, "w-full px-3 py-1.5 text-left hover:bg-muted/40")}
              >
                <ChevronRight className={cn("h-3 w-3 text-muted-foreground transition-transform", open && "rotate-90")} />
                <span className="truncate text-xs tabular-nums text-muted-foreground">{fmtWhen(e.ts)}</span>
                <span className="truncate" title={e.description}>
                  {e.description || "Agent activity"}
                </span>
                <span className="min-w-0">
                  <Badge variant="outline" className="text-xs text-muted-foreground">
                    {usageBadge(e)}
                  </Badge>
                </span>
                <span
                  className={cn(
                    "text-right text-xs tabular-nums",
                    credit ? "text-emerald-600 dark:text-emerald-400" : "text-foreground",
                  )}
                >
                  {credit ? "+" : "−"}
                  {fmtCredits(Math.abs(e.credits))}
                </span>
                <span className="text-right text-xs tabular-nums text-muted-foreground">
                  {fmtCredits(e.balance_after_credits)}
                </span>
              </button>
              {open && (
                <div className="px-3 pb-3 pt-1">
                  <UsageDetail entry={e} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {more && (
        <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
          <Spinner className="h-4 w-4" /> Loading more…
        </div>
      )}
      {!cursor && (
        <div className="py-3 text-center text-[11px] text-muted-foreground">End of history</div>
      )}
    </div>
  );
}

// UsageTab leads with the per-charge activity log (what the user expects from
// "usage"), then a window-scoped breakdown by agent and by
// conversation/channel/scheduled-task/infrastructure.
function UsageTab() {
  const [days, setDays] = useState(30);
  const [agentRows, setAgentRows] = useState<BarRow[]>([]);
  const [whereRows, setWhereRows] = useState<BarRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback((d: number) => {
    setLoading(true);
    setErr(null);
    Promise.all([listBreakdown(d), listSpendByAgent(d)])
      .then(([b, a]) => {
        setTotal(b.total_credits ?? 0);
        setWhereRows((b.lines ?? []).map((l: SpendLine) => ({ label: l.label, sub: lineBadge(l), credits: l.credits })));
        setAgentRows(
          (a.agents ?? [])
            .filter((x) => x.credits > 0)
            .map((x) => ({ label: x.name || "Untitled agent", credits: x.credits })),
        );
      })
      .catch((e) => setErr(humanizeError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(days);
  }, [load, days]);

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent activity</CardTitle>
          <CardDescription>Every credit charge, newest first — with the agent and the channel it ran through.</CardDescription>
        </CardHeader>
        <CardContent>
          <HistoryTable />
        </CardContent>
      </Card>

      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Breakdown · consumed <span className="font-medium text-foreground">{fmtCredits(total)}</span> credits in the last{" "}
            {days} days.
          </p>
          <div className="flex gap-1">
            {[7, 30].map((d) => (
              <Button key={d} size="sm" variant={days === d ? "default" : "outline"} onClick={() => setDays(d)}>
                {d}d
              </Button>
            ))}
          </div>
        </div>

        {err ? (
          <ErrorState message={err} onRetry={() => load(days)} />
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">By agent</CardTitle>
                <CardDescription>Which agent is consuming credits.</CardDescription>
              </CardHeader>
              <CardContent>
                <RankList rows={agentRows} loading={loading} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Where it went</CardTitle>
                <CardDescription>By conversation, channel, scheduled task, and infrastructure.</CardDescription>
              </CardHeader>
              <CardContent>
                <RankList rows={whereRows} loading={loading} />
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

export function Credits() {
  const [tab, setTab] = useState("overview");
  const [acct, setAcct] = useState<BillingAccount | null>(null);
  const [acctErr, setAcctErr] = useState<string | null>(null);
  const [agents, setAgents] = useState<AgentSpend[]>([]);
  const [isOwner, setIsOwner] = useState(false);

  const loadAccount = useCallback(() => {
    setAcctErr(null);
    getBillingAccount()
      .then(setAcct)
      .catch((e) => setAcctErr(humanizeError(e)));
  }, []);
  const loadAgents = useCallback(() => {
    listSpendByAgent(30)
      .then((r) => setAgents(r.agents ?? []))
      .catch(() => setAgents([]));
  }, []);

  useEffect(() => {
    loadAccount();
    loadAgents();
    getMe()
      .then((me) => {
        const role = me.workspaces?.find((w) => w.tenant_id === me.current_tenant_id)?.role;
        setIsOwner(me.auth === "api_key" || role === "owner");
      })
      .catch(() => setIsOwner(false));
  }, [loadAccount, loadAgents]);

  return (
    <div>
      <PageHeader
        title="Credits"
        description="Your workspace's credits, per-agent budgets, and where they're spent."
        action={
          isOwner ? (
            <Button asChild variant="outline">
              <Link to="/billing">Plans &amp; billing</Link>
            </Button>
          ) : undefined
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview &amp; budgets</TabsTrigger>
          <TabsTrigger value="history">Usage</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="space-y-8">
            {acctErr ? (
              <ErrorState message={acctErr} onRetry={loadAccount} />
            ) : acct ? (
              <BalanceCard acct={acct} />
            ) : (
              <Card>
                <CardContent className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
                  <Spinner /> Loading balance…
                </CardContent>
              </Card>
            )}
            <AgentBudgetsCard agents={agents} onChanged={loadAgents} />
          </div>
        </TabsContent>

        <TabsContent value="history">
          <UsageTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
